/**
 * ConnectionStatusButton
 *
 * A button component that displays the connection status between the current
 * user and another user, with appropriate action buttons based on the status.
 *
 * Status States:
 * - none: "Connect" button to send request
 * - pending_sent: "Request Sent" with cancel option
 * - pending_received: "Accept" / "Decline" buttons
 * - connected: "Connected" with remove option
 * - blocked: Shows blocked state
 * - self: Hidden (can't connect with yourself)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  UserCheck,
  UserMinus,
  Clock,
  Check,
  X,
  Loader2,
  MoreHorizontal,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useConnectionActions } from '@/stores/connection';
import type { UserConnectionStatus } from '@/types/connections';

interface ConnectionStatusButtonProps {
  userId: string;
  connectionId?: string;
  status: UserConnectionStatus;
  displayName: string;
  onStatusChange?: () => void;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

export const ConnectionStatusButton: React.FC<ConnectionStatusButtonProps> = ({
  userId,
  connectionId,
  status,
  displayName,
  onStatusChange,
  size = 'default',
  className,
}) => {
  const navigate = useNavigate();
  const { sendRequest, acceptRequest, declineRequest, cancelRequest, removeConnection, blockUser } =
    useConnectionActions();

  const [isLoading, setIsLoading] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);

  // Don't render for self
  if (status === 'self') {
    console.log('[ProfileConnections] Connection status for self - not showing button');
    return null;
  }

  // Handle send request
  const handleSendRequest = async () => {
    setIsLoading(true);
    console.log('[ProfileConnections] Sending connection request to user:', userId);

    try {
      await sendRequest(userId);
      toast.success('Connection request sent', {
        description: `Request sent to ${displayName}`,
      });
      onStatusChange?.();
    } catch (err) {
      console.error('[ProfileConnections] Error sending request:', err);
      toast.error('Failed to send request', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle accept request
  const handleAccept = async () => {
    if (!connectionId) return;

    setIsLoading(true);
    console.log('[ProfileConnections] Accepting connection request:', connectionId);

    try {
      await acceptRequest(connectionId);
      toast.success(`Connected with ${displayName}!`);
      onStatusChange?.();
    } catch (err) {
      console.error('[ProfileConnections] Error accepting request:', err);
      toast.error('Failed to accept request', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle decline request
  const handleDecline = async () => {
    if (!connectionId) return;

    setIsLoading(true);
    console.log('[ProfileConnections] Declining connection request:', connectionId);

    try {
      await declineRequest(connectionId);
      toast.success('Request declined');
      onStatusChange?.();
    } catch (err) {
      console.error('[ProfileConnections] Error declining request:', err);
      toast.error('Failed to decline request', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel sent request
  const handleCancel = async () => {
    if (!connectionId) return;

    setIsLoading(true);
    console.log('[ProfileConnections] Cancelling sent request:', connectionId);

    try {
      await cancelRequest(connectionId);
      toast.success('Request cancelled');
      onStatusChange?.();
    } catch (err) {
      console.error('[ProfileConnections] Error cancelling request:', err);
      toast.error('Failed to cancel request', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove connection
  const handleRemove = async () => {
    if (!connectionId) return;

    setIsLoading(true);
    setShowRemoveDialog(false);
    console.log('[ProfileConnections] Removing connection:', connectionId);

    try {
      await removeConnection(connectionId);
      toast.success('Connection removed', {
        description: `You are no longer connected with ${displayName}.`,
      });
      onStatusChange?.();
    } catch (err) {
      console.error('[ProfileConnections] Error removing connection:', err);
      toast.error('Failed to remove connection', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle block user
  const handleBlock = async () => {
    setIsLoading(true);
    setShowBlockDialog(false);
    console.log('[ProfileConnections] Blocking user:', userId);

    try {
      await blockUser(userId);
      toast.success('User blocked', {
        description: `${displayName} has been blocked.`,
      });
      onStatusChange?.();
    } catch (err) {
      console.error('[ProfileConnections] Error blocking user:', err);
      toast.error('Failed to block user', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  console.log('[ProfileConnections] Connection status for user:', userId, status);

  // Render based on status
  switch (status) {
    case 'none':
      return (
        <Button
          size={size}
          onClick={handleSendRequest}
          disabled={isLoading}
          className={cn('bg-primary text-primary-foreground hover:bg-primary/90', className)}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <UserPlus className="h-4 w-4 mr-2" />
          )}
          Connect
        </Button>
      );

    case 'pending_sent':
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <Button
            size={size}
            variant="secondary"
            disabled
            className="cursor-default"
          >
            <Clock className="h-4 w-4 mr-2" />
            Request Sent
          </Button>
          <Button
            size={size}
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </Button>
        </div>
      );

    case 'pending_received':
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <Button
            size={size}
            onClick={handleAccept}
            disabled={isLoading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Accept
          </Button>
          <Button
            size={size}
            variant="outline"
            onClick={handleDecline}
            disabled={isLoading}
          >
            <X className="h-4 w-4 mr-2" />
            Decline
          </Button>
        </div>
      );

    case 'connected':
      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size={size}
                variant="outline"
                className={cn('gap-2', className)}
                disabled={isLoading}
              >
                <UserCheck className="h-4 w-4 text-primary" />
                Connected
                <MoreHorizontal className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate(`/dashboard/connections`)}
              >
                <UserCheck className="h-4 w-4 mr-2" />
                View Connections
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowRemoveDialog(true)}
                className="text-destructive focus:text-destructive"
              >
                <UserMinus className="h-4 w-4 mr-2" />
                Remove Connection
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setShowBlockDialog(true)}
                className="text-destructive focus:text-destructive"
              >
                <Ban className="h-4 w-4 mr-2" />
                Block User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Remove confirmation dialog */}
          <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove Connection</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to remove {displayName} from your connections?
                  You can reconnect later by sending a new request.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRemove}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Block confirmation dialog */}
          <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Block User</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to block {displayName}? They won't be able to
                  send you connection requests or see your profile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleBlock}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Block
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      );

    case 'blocked':
      return (
        <Button
          size={size}
          variant="secondary"
          disabled
          className={cn('cursor-not-allowed opacity-60', className)}
        >
          <Ban className="h-4 w-4 mr-2" />
          Blocked
        </Button>
      );

    case 'blocked_by':
      // Don't show anything if blocked by the other user
      return null;

    default:
      return null;
  }
};

export default ConnectionStatusButton;
